import { postToPresignedUrl } from '../../api'
import { HTTP_TOO_MANY_REQUESTS } from '../../arch/constants'
import { createIepReviewUpload } from '../../auth'
import { getIepReviewErrorMessage } from '../../domain/iepReview'
import { MIME_TYPE_PNG, convertHeic, isPotentialHeic, toJpegFile } from '../../subcomponents/support/imageFiles'

import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_TYPES_LABEL,
  MAX_UPLOAD_FILE_BYTES,
  MAX_UPLOAD_FILE_NAME_LENGTH,
  MAX_UPLOAD_FILES,
  PDF_MIME_TYPE,
  UPLOAD_MIME_TYPES
} from './constants'

const UPLOAD_DIMENSION_MAX = 2400
const UPLOAD_JPEG_QUALITY = 0.85

const UPLOAD_LIMIT_ERROR = 'We can’t take more uploads right now. Please try again tomorrow.'
const UNREADABLE_FILE_ERROR = 'We couldn’t read that file. Try a PDF, JPG or PNG.'

const MAX_FILE_SIZE_LABEL = `${MAX_UPLOAD_FILE_BYTES / (1024 * 1024)} MB`
const FILE_TOO_LARGE_ERROR = `Each file must be under ${MAX_FILE_SIZE_LABEL}. Try a smaller scan, or photos of each page.`
const TOO_MANY_FILES_ERROR = `You can upload up to ${MAX_UPLOAD_FILES} files.`
const UNSUPPORTED_FILE_ERROR = `That file type isn’t supported. Please upload a ${ACCEPTED_FILE_TYPES_LABEL}.`
const INLINE_UPLOAD_ERRORS = [UNREADABLE_FILE_ERROR, UPLOAD_LIMIT_ERROR]

const REJECTION_REASONS = {
  [FILE_TOO_LARGE_ERROR]: 'fileTooLarge',
  [TOO_MANY_FILES_ERROR]: 'tooManyFiles',
  [UNSUPPORTED_FILE_ERROR]: 'unsupportedType',
  [UNREADABLE_FILE_ERROR]: 'unreadable',
  [UPLOAD_LIMIT_ERROR]: 'rateLimited'
}

const isAcceptedFile = file => ACCEPTED_FILE_TYPES.includes(file.type) || isPotentialHeic(file)

const isOversizedPdf = file => file.type === PDF_MIME_TYPE && file.size > MAX_UPLOAD_FILE_BYTES

const selectionErrorFor = files => {
  if (files.length > MAX_UPLOAD_FILES) {
    return TOO_MANY_FILES_ERROR
  }

  if (!files.every(isAcceptedFile)) {
    return UNSUPPORTED_FILE_ERROR
  }

  return files.some(isOversizedPdf) ? FILE_TOO_LARGE_ERROR : null
}

// We convert to PNG rather than JPEG because PNG keeps transparency for toJpegFile to fill with white, whereas a
// direct JPEG decode turns it black.
const heicToPng = async file => {
  try {
    return await convertHeic(file, MIME_TYPE_PNG)
  } catch (error) {
    return null
  }
}

const normalizeUploadFile = async file => {
  if (file.type === PDF_MIME_TYPE) {
    return file
  }

  const image = isPotentialHeic(file) ? await heicToPng(file) : file
  if (!image) {
    return file
  }

  const imageSrc = URL.createObjectURL(image)

  try {
    return await toJpegFile({
      imageSrc,
      fileName: file.name,
      dimensionMax: UPLOAD_DIMENSION_MAX,
      quality: UPLOAD_JPEG_QUALITY
    })
  } finally {
    URL.revokeObjectURL(imageSrc)
  }
}

// A scanner can produce a name longer than the server stores, which would otherwise fail the upload with a
// generic error, so the name is shortened ahead of its extension.
const uploadFileName = name => {
  if (name.length <= MAX_UPLOAD_FILE_NAME_LENGTH) {
    return name
  }

  const extensionStart = name.lastIndexOf('.')
  const extension = extensionStart > 0 ? name.slice(extensionStart) : ''

  return `${name.slice(0, MAX_UPLOAD_FILE_NAME_LENGTH - extension.length)}${extension}`
}

const uploadIepReviewFile = async originalFile => {
  const file = await normalizeUploadFile(originalFile)
  if (!UPLOAD_MIME_TYPES.includes(file.type)) {
    throw new Error(UNREADABLE_FILE_ERROR)
  }

  const { url, stagingKey } = await createIepReviewUpload({
    fileName: uploadFileName(file.name),
    fileSize: file.size,
    mimeType: file.type
  })

  const uploaded = stagingKey && (await postToPresignedUrl(url, file))
  if (!uploaded) {
    // The request never reached the server and the global error dialog already says so, so marking the error as
    // reported stops the upload without showing a second one.
    throw Object.assign(new Error(), { alreadyReported: true })
  }

  return { id: stagingKey, file }
}

const getUploadErrorMessage = error => {
  if (error.response?.status === HTTP_TOO_MANY_REQUESTS) {
    return UPLOAD_LIMIT_ERROR
  }

  if (error.alreadyReported) {
    return null
  }

  if (error.message === UNREADABLE_FILE_ERROR) {
    return error.message
  }

  return getIepReviewErrorMessage({ message: error.message }, error.response?.status)
}

export {
  UPLOAD_LIMIT_ERROR,
  UNREADABLE_FILE_ERROR,
  MAX_FILE_SIZE_LABEL,
  INLINE_UPLOAD_ERRORS,
  REJECTION_REASONS,
  selectionErrorFor,
  normalizeUploadFile,
  uploadFileName,
  uploadIepReviewFile,
  getUploadErrorMessage
}
